import { HonestWork, PopupDirection } from '@cian/honestwork-component';
import { OwnerBadge, TooltipDirection } from '@cian/owner-badge-component';
import * as React from 'react';

import { IAgentSchema, IOfferSchema } from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { LinkWrapper } from '../../../../common/components/link_wrapper';
import { Tag } from '../../../../common/components/tag';
import { IUserLevelInfoSchema } from '../../../../repositories/offer-card/entities/agent/UserLevelInfoSchema';
import {
  getAgentLabel,
  getAuthorLabel,
  getTrackingLabelPrefix,
  getVerifyWay,
  isHomeownerVerified,
  getAgentName,
} from '../../../../utils/agent';
import { createAgentIdName } from '../../../../utils/createAgentIdName';
import { PartnerTooltip } from '../../../PartnerTooltip';
import { UserWorkHonestlyInfo } from '../../../UserWorkHonestlyInfo';
import { getCardType, trackGoToCard } from '../../../author_aside_brand/tracking';
import * as Analytics from '../../tracking';
import * as styles from '../Author/index.css';

export interface IHonestProps {
  profileLink?: string;
  agent: IAgentSchema;
  offer: IOfferSchema;
  isHonestAgent: boolean;
  isProAgent: boolean;
  isCianPartner?: boolean | null;
  agentLevelInfo?: IUserLevelInfoSchema | null;
  showCountrysideBuilderBadge: boolean;
}

export const Honest = ({
  profileLink,
  agent,
  isHonestAgent,
  offer,
  isCianPartner,
  isProAgent,
  agentLevelInfo,
  showCountrysideBuilderBadge,
}: IHonestProps) => {
  const handleAgentNameClick = React.useCallback(
    () => trackGoToCard(getCardType(offer), 'name', 'agent_block'),
    [offer],
  );
  const nameHandler = profileLink ? handleAgentNameClick : undefined;
  const agentId = React.useMemo(() => createAgentIdName(agent), [agent]);
  const isVerified = React.useMemo(() => isHomeownerVerified(offer, agent), [offer, agent]);
  const verifyWay = React.useMemo(() => getVerifyWay(offer, agent), [offer, agent]);
  const label = React.useMemo(
    () => getAuthorLabel(offer, showCountrysideBuilderBadge) || getAgentLabel(agent.accountType, offer),
    [offer, agent, showCountrysideBuilderBadge],
  );
  const name = React.useMemo(() => getAgentName(agent, offer), [offer, agent]);

  return (
    <div className={styles['header']}>
      {agentLevelInfo && (
        <UserWorkHonestlyInfo
          agentLevelInfo={agentLevelInfo}
          agentName={agent.bank || name || agentId}
          nameHandler={nameHandler}
          isAgentPublished={!!profileLink}
          profileLink={profileLink}
          isProAgent={isProAgent}
          agent={agent}
        />
      )}
      {!agentLevelInfo && (
        <LinkWrapper
          className={styles['title-link']}
          href={profileLink}
          target="_blank"
          onClick={() => Analytics.trackGoToCard(`${getTrackingLabelPrefix(offer)}name`)}
        >
          <h2 className={styles['title']}>{name || `ID ${agent.id}`}</h2>
        </LinkWrapper>
      )}
      {!agentLevelInfo && isHonestAgent && (
        <HonestWork
          agentLink={{
            agentId: agent.id,
            otherLinkProps: {
              deal_type: offer.dealType,
              offer_type: offer.offerType,
            },
          }}
          direction={PopupDirection.RIGHT}
          className={styles['honest']}
        />
      )}
      {!agentLevelInfo && isProAgent && (
        <Tag className={styles['tag']} title="Профессиональный пользователь">
          Pro
        </Tag>
      )}
      {isCianPartner && (
        <div className={styles['tag']}>
          <PartnerTooltip master={<Tag className={styles['tag-partner']}>Партнёр</Tag>} />
        </div>
      )}
      {label === 'homeowner' ? (
        <div className={styles['tag']}>
          <OwnerBadge isVerified={isVerified} direction={TooltipDirection.RIGHT} verifyWay={verifyWay} />
        </div>
      ) : (
        <Tag className={styles['tag']}>{label}</Tag>
      )}
    </div>
  );
};
